import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { UserDAO } from '../../../../../../../core/services/dao/userDAO';
import { User } from 'anchor-shared/lib/model/entity/user';
import { YouTubeValidationService } from '../../../../../../../core/services/youtube-validation.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { VesselImage } from 'anchor-shared/lib/model/entity/vesselImage';
import { VesselImageDAO } from '../../../../../../../core/services/dao/vesselImageDAO';
import {
  BucketUploadResponse,
  FileSelectionError,
  getFileNameWithExtension,
  ImageType,
  MediaType,
  MultiUploadConfig,
} from '../../../../../../../core/pure-functions/upload/image-upload-helper';
import { ErrorDialogService } from '../../../../../../shared/errorDialog/errorDialogService';
import { DialogsService } from '../../../../../../../core/services/dialog.service';
import { UploadDAO } from '../../../../../../../core/services/dao/uploadDAO';
import { BadFile } from '../../../../../../shared/upload/multi-file-upload/multi-file-upload.component';
import { OrderVesselImagesByPipe } from '../../../../../../shared/pipes/order-vessel-images-by.pipe';
import { ContainsSubscriptions } from '../../../../../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs/Rx';
import { unsubscribeFromAll } from '../../../../../../../core/pure-functions/rx-helper';

@Component({
  selector: 'app-photos-and-video-form',
  templateUrl: './photos-and-video.component.html',
  styleUrls: ['./photos-and-video.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class PhotosAndVideoFormComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Input() model: Vessel;
  @Input() userId: number;
  @Input() form: NgForm;
  @Input() disabled: boolean;
  @Output()
  sortOrderChanged: EventEmitter<VesselImage> = new EventEmitter<VesselImage>();
  @Output()
  primaryPhotoChanged: EventEmitter<string> = new EventEmitter<any>();

  user: User;
  isAdmin = false;
  videoLink: string;
  displayYouTubeIFrame = false;
  validatedIFrameLink;
  photoExtensionTypes: ImageType[];
  multiUploadConfig: MultiUploadConfig;
  youtubeValidatorRegex: string;
  allowedMediaType = MediaType.IMAGE;
  vesselImages: VesselImage[] = [];
  nonUploadedFilesSmall: string[] = [];
  nonUploadedFilesLarge: string[] = [];
  s3path: string;
  paramId: number;
  refreshSortOrder: Subject<VesselImage[]> = new Subject();
  subs: Subscription[] = [];
  photoVideoModel: Partial<Vessel>;

  constructor(
    private userDAO: UserDAO,
    private youtubeValidator: YouTubeValidationService,
    public vesselImageDAO: VesselImageDAO,
    private errorDialogService: ErrorDialogService,
    public dialog: DialogsService,
    public uploadDAO: UploadDAO,
    private orderVesselImagesByPipe: OrderVesselImagesByPipe,
  ) {
    this.youtubeValidatorRegex = this.youtubeValidator.youtubeRegex;
    this.photoExtensionTypes = [ImageType.JPEG, ImageType.JPG, ImageType.PNG];
    this.multiUploadConfig = {
      maxFileSizeMb: 10,
      maxFileSelection: 30,
    };
  }

  ngOnInit() {
    const imageSub = this.vesselImageDAO
      .getAllByVesselId(this.model.id)
      .subscribe((vesselImages) => {
        this.vesselImages = this.orderVesselImagesByPipe.transform(
          vesselImages,
          'sortOrder',
        );
      });
    this.user = this.userDAO.getCurrentUser();
    this.user.roles.map((role) => {
      if (role.adminAccess) {
        this.isAdmin = true;
      }
    });
    this.subs.push(imageSub);
    this.s3path = 'vesselphotos/' + this.model.id;
    this.paramId = this.isAdmin ? this.model.owner.userId : null;
    this.videoLink = this.model.youtubeLink;
    const link = this.youtubeValidator.validateTransformAndSanitizeYouTubeLink(
      this.videoLink,
    );
    this.setUI(link);
  }

  setPrimaryPhoto(url) {
    this.model.primaryPhotoAbsPath = url;
    this.setModel();
    this.primaryPhotoChanged.emit(url);
  }

  sortOrderChange(vesselImage: VesselImage): void {
    this.sortOrderChanged.emit(vesselImage);
  }

  resourceCreated(newResource: VesselImage): void {
    this.vesselImages.push(newResource);
  }

  onUploadComplete(files: BadFile[]): void {
    files.forEach((file) => {
      if (file.reason === 'small') {
        this.nonUploadedFilesSmall.push(file.file.name);
      } else if (file.reason === 'large') {
        this.nonUploadedFilesLarge.push(file.file.name);
      }
    });
  }

  displayFileSelectionError(error: FileSelectionError): void {
    // this.errorDialogService.open('File Selection Error', error.message);
  }

  public onBlur(): void {
    const link = this.youtubeValidator.validateTransformAndSanitizeYouTubeLink(
      this.videoLink,
    );
    this.setUI(link);
  }

  private setUI(link: SafeResourceUrl): void {
    if (link) {
      this.validatedIFrameLink = link;
      this.displayYouTubeIFrame = true;
      this.model.youtubeLink = this.videoLink;
    } else {
      this.displayYouTubeIFrame = false;
      this.validatedIFrameLink = null;
      if (
        this.form.controls &&
        this.form.controls.videoLink &&
        this.form.controls.videoLink.value !== '' &&
        this.form.controls.videoLink.touched
      ) {
        this.form.controls.videoLink.setErrors({ valid: false });
      }
      this.model.youtubeLink = '';
    }
    this.setModel();
  }

  openCropper(vesselImage: VesselImage) {
    this.dialog
      .ImageCropper(vesselImage, this.model.id, vesselImage.id)
      .subscribe((uploadResponses: BucketUploadResponse[]) => {
        if (uploadResponses) {
          vesselImage.url = uploadResponses[0].url;
          vesselImage.resourceKey = uploadResponses[0].key;
          vesselImage.thumbUrl = uploadResponses[0].url;
          vesselImage.thumbKey = uploadResponses[0].key;
          vesselImage.needsCrop = false;
          this.refreshSortOrder.next(this.vesselImages);
        }
      });
  }

  delete(image: VesselImage, index: number) {
    const ref = this.dialog.VesselImageDelete();
    ref.subscribe((res) => {
      if (res) {
        this.vesselImageDAO.serverDelete(image).subscribe(
          (result) => {
            console.log(result);
          },
          (err) => console.error(err),
        );

        this.uploadDAO
          .delete(
            getFileNameWithExtension(image.url),
            this.paramId,
            this.s3path,
          )
          .subscribe(
            (result) => {
              const segments = getFileNameWithExtension(image.url).split('.');
              segments.pop();
              segments.push('thumb');
              this.uploadDAO
                .delete(segments.join('.'), this.paramId, this.s3path)
                .subscribe(
                  () => {
                    this.vesselImages.splice(index, 1);
                    this.refreshSortOrder.next(this.vesselImages);
                  },
                  (err) => {
                    console.error(err);
                  },
                );
            },
            (err) => {
              console.error(err);
            },
          );
      }
    });
  }

  oneNeedsCrop(): boolean {
    return this.vesselImages.some((item) => item.needsCrop);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private setModel() {
    this.photoVideoModel = {
      id: this.model.id,
      youtubeLink: this.model.youtubeLink,
      primaryPhotoAbsPath: this.model.primaryPhotoAbsPath,
    };
  }
}
